/**
 * editInvite — RPC contract for the editInvite callable.
 *
 * `invite.entity.ts` marks `edit` `.requiresHandler('editInvite')` — the same
 * cross-document target-role containment createInvite enforces at mint (the
 * NEW role's permissions must be ⊆ the caller's own) is what a declarative
 * Firestore `.when(...)` rule cannot express, so `editInvite.api.ts` (onCall)
 *
 * Fields justified:
 *   token — the pending invite to edit; doc id of
 *           `workspaces/{orgId}/invites/{token}`. The handler rejects a
 *           token that doesn't resolve to a `pending` invite (already
 *           redeemed/revoked/expired) — editing a resolved invite is a CAS
 *           violation, not a silent no-op.
 *   patch — partial: a role correction, a name correction, or both. Both
 *           fields nullish rather than optional — Firestore/JSON round-trip
 *           `null` for an absent value the same way an omitted key does,
 *           matching editRole.action.ts's own convention.
 *
 * Excluded:
 *   scope — the stated need is role + name; scope-editing interacts with the
 *           same tenancy re-check createInvite/resendInvite run and doesn't
 *           earn its keep in this pass. Follow-up.
 *   email / invitedBy / createdAt / expiresAt / status — untouched by this
 *           callable; a role or name correction never re-tokens the invite,
 *           re-sends the invitation email, or changes its lifecycle state.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const EditInviteInput = z
	.object({
		orgId: Id,
		token: Id.describe(
			"the pending invite to edit — doc id of workspaces/{orgId}/invites/{token}",
		),
		patch: z.object({
			roleId: Id.nullish().describe(
				"when present, must exist in the org catalog and be ⊆ the caller's own role's permissions",
			),
			displayName: z.string().min(1).nullish(),
		}),
	})
	.strict();
export type EditInviteInput = z.infer<typeof EditInviteInput>;

export const EditInviteOutput = z.object({ ok: z.literal(true) }).strict();
export type EditInviteOutput = z.infer<typeof EditInviteOutput>;
